/*
 * Copyright (c) 2023 - 2026. Bluemate B.V.
 */

import React, {
    FocusEventHandler,
    InputHTMLAttributes,
    KeyboardEventHandler,
    ReactNode,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

import {ChangeFunction, ErrorType, generateComponentId, NumberString, OrnamentShape, roundTo} from '@dvrd/dvr-controls';

import {BluemateInput} from '../index';

import {isPasting, positionIsAfterComma, textIsSelected} from '../../../util/inputUtil';

import {BluemateControlShape, BluemateControlSize} from '../../../util/types';

interface Props {
    onChange: ChangeFunction<NumberString>;
    onFocus?: FocusEventHandler;
    onBlur?: FocusEventHandler;
    onKeyDown?: KeyboardEventHandler;
    onEnter?: KeyboardEventHandler;
    value: string | number | NumberString;
    label?: string | ReactNode;
    ornaments?: OrnamentShape | Array<OrnamentShape>;
    inputProps?: InputHTMLAttributes<any>;
    disabled?: boolean;
    autoFocus?: boolean;
    autoSelect?: boolean;
    className?: string;
    inputClassName?: string;
    labelClassName?: string;
    ornamentClassName?: string;
    errorClassName?: string;
    error?: ErrorType;
    id?: string;
    wholeNumbers?: boolean;
    asCurrency?: boolean;
    disableSymbols?: boolean;
    unControlled?: boolean;
    preventDefaultChangeEvent?: boolean;
    /** Forwarded to the underlying BluemateInput (props are spread through). */
    size?: BluemateControlSize;
    shape?: BluemateControlShape;
    uniformFont?: boolean;
}

export default function BluemateNumberInput(props: Props) {
    const {
        disableSymbols,
        value,
        onChange,
        onKeyDown,
        asCurrency,
        wholeNumbers,
        unControlled,
        preventDefaultChangeEvent
    } = props;
    const [innerValue, setInnerValue] = useState(new NumberString(value));
    const [inputProps, min, max] = useMemo(() => {
        const inputProps = {...props.inputProps};
        if (!inputProps.type) inputProps.type = 'number';
        const min = inputProps.min !== undefined ? Number(inputProps.min) : undefined;
        const max = inputProps.max !== undefined ? Number(inputProps.max) : undefined;
        return [inputProps, min, max];
    }, [props.inputProps]);
    const id = useRef(generateComponentId(props.id));

    function _onKeyDown(evt: React.KeyboardEvent<HTMLInputElement>) {
        const {key} = evt,
            numValue = innerValue.numberValue;
        // Allow only number and simple edit keys
        const editKeys = /\d|Backspace|ArrowRight|ArrowLeft|ArrowUp|ArrowDown|Escape|Esc|Enter|[,.\-+]|Tab/;
        if (!editKeys.test(key) && !isPasting(evt)) evt.preventDefault();

        // Handle Arrow events
        if (['ArrowUp', 'ArrowDown'].includes(key)) {
            evt.preventDefault();
            if (!isNaN(numValue)) return onArrowEvent(evt, numValue);
        }

        // Handle event if no selected text
        onKeyDownSelected(evt);

        if (onKeyDown) onKeyDown(evt);
    }

    function onArrowEvent(evt: React.KeyboardEvent<HTMLInputElement>, numValue: number) {
        const {key} = evt,
            step = Number(inputProps?.step || asCurrency ? 0.01 : 1);
        if (key === 'ArrowUp' && (max === undefined || numValue + step <= max)) numValue += step;
        else if (key === 'ArrowDown' && (min === undefined || numValue - step >= min)) numValue -= step;
        let stringValue = numValue.toString();
        if (asCurrency) stringValue = roundTo(numValue, 2);
        change(stringValue);
    }

    function onKeyDownSelected(evt: React.KeyboardEvent<HTMLInputElement>) {
        if (textIsSelected(evt)) return;
        const {currentTarget, key} = evt,
            position = currentTarget.selectionStart;
        if (/[,.]/.test(key) && /[,.]/.test(innerValue.stringValue) && asCurrency)
            // Prevent double points or commas
            evt.preventDefault();

        if (wholeNumbers && /[.,]/.test(key)) evt.preventDefault();
        else if (
            asCurrency &&
            /[.,]\d{2}/.test(innerValue.stringValue) &&
            /\d/.test(key) &&
            positionIsAfterComma(position, innerValue.stringValue)
        )
            // Prevent more than 2 decimals when working with currencies
            evt.preventDefault();

        if (/[-+]/.test(key) && (disableSymbols || (position && position !== 0)))
            // Prevent - or + sign completely or after first position
            evt.preventDefault();
    }

    function _onChange(value: string, evt: React.ChangeEvent<HTMLInputElement>) {
        if (preventDefaultChangeEvent) evt.preventDefault();
        value = value.replace(/ /g, '');
        if (!/^-?[\d.,]*$/.test(value)) evt.preventDefault();
        else {
            if (value.startsWith('.') || value.startsWith(',')) value = '0' + value;
            if (wholeNumbers)
                // Only allow numbers and minus sign for negative numbers
                value = value.replace(/[^0-9-]/g, '').trim();
            if (disableSymbols) value = value.replace(/[-+]/g, '').trim();
            change(value, evt);
        }
        return value;
    }

    function change(value: number | string, evt?: React.ChangeEvent<HTMLInputElement>) {
        const numberValue = new NumberString(value);
        setInnerValue(numberValue);
        onChange(numberValue, evt);
    }

    function _onBlur(evt: React.FocusEvent) {
        if (!unControlled && !showsValue()) setInnerValue(new NumberString(value));
        props.onBlur?.(evt);
    }

    function focusInput() {
        document.getElementById(id.current + '-input')?.focus();
    }

    // A parent that stores the parsed number cannot hold "12," or an emptied
    // field; the typed text stays as long as it still reads as that number.
    function showsValue(): boolean {
        if (new NumberString(value).stringValue === innerValue.stringValue) return true;
        return typeof value === 'number' && innerValue.nullNumberValue === value;
    }

    useEffect(() => {
        if (!unControlled && !showsValue()) setInnerValue(new NumberString(value));
    }, [value]);

    useEffect(() => {
        if (!props.disabled && props.autoFocus) focusInput();
    }, [props.disabled, props.autoFocus]);

    return (
        <BluemateInput
            {...props}
            id={id.current}
            onChange={_onChange}
            onKeyDown={_onKeyDown}
            onBlur={_onBlur}
            value={innerValue.stringValue}
            inputProps={inputProps}
        />
    );
}
